import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useUpdateDoctor } from "@/services/query/doctors.query"
import { useEffect, useState } from "react"
import { toast } from "sonner"
import { Users2, GraduationCap, Phone, BadgeCheck, MapPin } from "lucide-react"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { LOCATIONS } from "@/lib/constants"

export function EditDoctorDialog({ doctor, open, onOpenChange }) {
  const [formData, setFormData] = useState({
    name: "",
    designation: "",
    qualifications: "",
    phone: "",
    location: "",
    availability: "",
    meta: {},
    schedule: {},
  })

  const updateDoctorMutation = useUpdateDoctor()

  useEffect(() => {
    if (doctor && open) {
      setFormData({
        name: doctor.name || "",
        designation: doctor.designation || "",
        qualifications: doctor.qualifications || "",
        phone: doctor.phone || "",
        location: doctor.location || "",
        availability: doctor.availability || "",
        meta: doctor.meta || {},
        schedule: doctor.schedule || {},
      })
    }
  }, [doctor, open])

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      await updateDoctorMutation.mutateAsync({
        id: doctor.id,
        data: formData,
      })
      onOpenChange(false)
      toast.success("Doctor updated successfully")
    } catch (error) {
      toast.error(error.message || "Failed to update doctor")
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users2 className="h-5 w-5 text-blue-500" />
            Edit Doctor
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-6 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name" className="flex items-center gap-2">
                <BadgeCheck className="h-4 w-4 text-blue-500" />
                Full Name
              </Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="border-blue-100 focus:border-blue-200"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label
                  htmlFor="designation"
                  className="flex items-center gap-2"
                >
                  <Users2 className="h-4 w-4 text-blue-500" />
                  Designation
                </Label>
                <Input
                  id="designation"
                  name="designation"
                  value={formData.designation}
                  onChange={handleChange}
                  className="border-blue-100 focus:border-blue-200"
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="phone" className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-blue-500" />
                  Phone Number
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="border-blue-100 focus:border-blue-200"
                  required
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label
                htmlFor="qualifications"
                className="flex items-center gap-2"
              >
                <GraduationCap className="h-4 w-4 text-blue-500" />
                Qualifications
              </Label>
              <Textarea
                id="qualifications"
                name="qualifications"
                value={formData.qualifications}
                onChange={handleChange}
                className="border-blue-100 focus:border-blue-200 min-h-[80px]"
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="location" className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-blue-500" />
                Location
              </Label>
              <Select
                name="location"
                value={formData.location}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, location: value }))
                }
                required
              >
                <SelectTrigger className="border-blue-100 focus:border-blue-200">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {LOCATIONS.map((location) => (
                    <SelectItem key={location} value={location}>
                      {location}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={updateDoctorMutation.isPending}
              className="bg-blue-500 hover:bg-blue-600"
            >
              {updateDoctorMutation.isPending ? "Updating..." : "Update Doctor"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
